import { AnimatePresence, motion } from 'framer-motion';
import { X, Shield, Brain, Users, DollarSign, AlertTriangle, Calendar, ArrowRightLeft } from 'lucide-react';

interface GouvernanceComptesAIOAuthRevisionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function GouvernanceComptesAIOAuthRevisionModal({ isOpen, onClose }: GouvernanceComptesAIOAuthRevisionModalProps) {
  if (!isOpen) return null;

  const highlights = [
    { icon: Brain, title: 'Standard Master', text: 'Cadre global PRIsME Master, non client, pour le modèle OAuth agentique.' },
    { icon: Users, title: 'Ownership', text: '1 agent = 1 humain = 1 compte OpenAI OAuth, sans comptes partagés.' },
    { icon: DollarSign, title: 'Coût fixe', text: 'Passage d’un modèle variable par tokens vers un coût fixe prévisible.' },
    { icon: AlertTriangle, title: 'Limites assumées', text: 'Quotas 5h et hebdomadaires, contraintes fair-use, pas orienté temps réel.' },
    { icon: Calendar, title: 'Rituels', text: 'Revue usage hebdo, ROI mensuel par agent, audit périodique des accès.' },
    { icon: ArrowRightLeft, title: 'Statut', text: 'Carte créée en contenu en révision avant promotion dans la structure PRIsME officielle.' },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.98, y: 8 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl border border-indigo-500/20 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 shadow-2xl"
        >
          <div className="sticky top-0 z-20 bg-slate-950/90 backdrop-blur border-b border-indigo-500/20 px-6 py-4 flex items-start justify-between gap-4">
            <div>
              <p className="uppercase tracking-[0.25em] text-indigo-300 text-xs mb-1">Contenu en révision</p>
              <h2 className="text-2xl font-bold text-white">Gouvernance des comptes AI & modèle OAuth Agentic (AIKUB)</h2>
              <p className="text-gray-400 mt-1 text-sm max-w-3xl">
                Carte de gouvernance Master proposée pour formaliser le modèle OAuth agentique comme standard PRIsME global.
              </p>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-indigo-300" />
                Positionnement
              </h3>
              <p className="text-sm text-gray-300 leading-relaxed">
                Cette proposition ne traite ni d’un client, ni d’un workflow d’exécution. Elle définit un cadre de gouvernance Master pour standardiser l’ownership, la documentation, les coûts et le scaling des agents opérés sous OAuth.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {highlights.map(({ icon: Icon, title, text }) => (
                <div key={title} className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                    <Icon className="w-4 h-4 text-indigo-300" />
                    {title}
                  </h4>
                  <p className="text-sm text-gray-300 leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
